import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import UserForm from '../../components/Auth/UserForm';
import { SystemUser, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useSystemUsers, useCreateSystemUser, useUpdateSystemUser, useDeleteSystemUser } from '../../hooks/useSystemUsers';
import { useRoles } from '../../hooks/useRoles';

const SystemUsersManagement: React.FC = () => {
  // Memoize options to prevent infinite re-renders
  const usersOptions = useMemo(() => ({
    pageSize: 100,
    page: 1,
    sort: 'firstname'
  }), []);

  // Fetch roles from API
  const { data: roles = [], isLoading: rolesLoading } = useRoles();

  // Fetch users from API (with pagination and sorting)
  const { data: users = [], isLoading: usersLoading, error: usersError } = useSystemUsers(usersOptions);

  // Mutations for CRUD operations
  const createMutation = useCreateSystemUser();
  const updateMutation = useUpdateSystemUser();
  const deleteMutation = useDeleteSystemUser();

  // Local state - must be before early returns
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | undefined>();

  // Loading and error states - NOW we can have early returns
  if (usersLoading || rolesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading system users...</div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Error loading system users. Please try again.</div>
      </div>
    );
  }

  const columns: TableColumn[] = [
    // Strapi admin users sign in with email; username is always null
    { key: 'username', label: 'Username', sortable: true,
      render: (value: string, item: SystemUser) => value || item.email },
    { key: 'firstName', label: 'Full Name', sortable: true, render: (_: string, item: SystemUser) => `${item.firstName} ${item.lastName}` },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'roleName', label: 'Role', sortable: true, render: (value: string) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {value}
      </span>
    )},
    { key: 'department', label: 'Department', sortable: true },
    { key: 'phone', label: 'Phone', sortable: false },
    { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? 'Active' : 'Inactive'}
      </span>
    )},
    { key: 'lastLogin', label: 'Last Login', sortable: true, render: (value: string) => 
      value ? new Date(value).toLocaleDateString() + ' ' + new Date(value).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Never'
    },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const handleEdit = (user: SystemUser) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (user: SystemUser) => {
    if (user.username === 'admin') {
      alert('The system administrator account cannot be deleted.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      await deleteMutation.execute(user);
    }
  };

  const handleAdd = () => {
    setEditingUser(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<SystemUser>) => {
    if (editingUser) {
      // Update existing user
      await updateMutation.execute({ id: editingUser.id, data });
    } else {
      // Add new user
      await createMutation.execute(data);
    }
    setShowForm(false);
    setEditingUser(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(undefined);
  };

  const handleExport = () => {
    const exportData = users.map((user: SystemUser) => ({
      'Username': user.username,
      'First Name': user.firstName,
      'Last Name': user.lastName,
      'Email': user.email,
      'Role': user.roleName,
      'Status': user.isActive ? 'Active' : 'Inactive',
      'Last Login': user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never',
      'Created': new Date(user.createdAt).toLocaleDateString()
    }));
    
    exportToExcel(exportData, `system_users_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = users.map((user: SystemUser) => ({
      'Username': user.username,
      'First Name': user.firstName,
      'Last Name': user.lastName,
      'Email': user.email,
      'Role': user.roleName,
      'Status': user.isActive ? 'Active' : 'Inactive',
      'Last Login': user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never',
      'Created': new Date(user.createdAt).toLocaleDateString()
    }));
    
    printAsExcel(exportData, 'System Users');
  };

  // Statistics
  const activeUsers = users.filter((user: SystemUser) => user.isActive).length;
  const inactiveUsers = users.filter((user: SystemUser) => !user.isActive).length;
  const recentLogins = users.filter((user: SystemUser) => 
    user.lastLogin && 
    new Date(user.lastLogin) > new Date(Date.now() - 24 * 60 * 60 * 1000)
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Users Management</h1>
          <p className="text-gray-600">Manage system users, their roles, and access permissions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
          >
            Export
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Print
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{users.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{activeUsers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Inactive Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{inactiveUsers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Recent Logins (24h)</dt>
                  <dd className="text-lg font-medium text-gray-900">{recentLogins}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Security Note:</strong> Users inherit permissions from their assigned roles. 
              Changing a user's role will immediately update their access permissions. 
              The system administrator account cannot be deleted for security reasons.
            </p>
          </div>
        </div>
      </div>
      
      <Table
        columns={columns}
        data={users}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="System Users"
        searchPlaceholder="Search users..."
      />
      
      {showForm && (
        <UserForm
          user={editingUser}
          roles={roles}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default SystemUsersManagement;